/**
 * Shipments API Functions
 * Main shipment CRUD operations and KPIs
 */

import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString } from './client';
import type {
  Shipment,
  CreateShipmentDto,
  UpdateShipmentDto,
  UpdateShipmentStatusDto,
  ShipmentFilters,
  ShipmentKpis,
} from '../types/shipments';

const BASE = '/api/shipments';

export interface PaginatedShipmentList {
  items: Shipment[];
  pagination: PaginationMetadata | null;
}

/**
 * List all shipments with optional filters and pagination
 */
export const listShipments = async (
  filters?: ShipmentFilters
): Promise<PaginatedShipmentList> => {
  const query = buildQueryString(filters as Record<string, unknown> | undefined);
  return apiFetch<PaginatedShipmentList>(`${BASE}/${query}`);
};

/**
 * Get a single shipment by ID
 */
export const getShipment = async (id: string): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}`);
};

/**
 * Create a new shipment
 */
export const createShipment = async (data: CreateShipmentDto): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Update an existing shipment
 */
export const updateShipment = async (
  id: string,
  data: UpdateShipmentDto
): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Update shipment status
 */
export const updateShipmentStatus = async (
  id: string,
  data: UpdateShipmentStatusDto
): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * Cancel a shipment
 */
export const cancelShipment = async (
  id: string,
  reason?: string
): Promise<Shipment> => {
  return apiFetch<Shipment>(`${BASE}/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
};

/**
 * Get shipment KPIs
 */
export const getShipmentKpis = async (): Promise<ShipmentKpis> => {
  return apiFetch<ShipmentKpis>(`${BASE}/kpis`);
};

/**
 * Delete a shipment
 */
export const deleteShipment = async (id: string): Promise<void> => {
  return apiFetch<void>(`${BASE}/${id}`, {
    method: 'DELETE',
  });
};
